/**
 * run-xp.ts — per-run experience / level state for the horizontal battle.
 *
 * roguelite-xp (2026-05-31, boss 指定「經典 roguelite 經驗條三選一」): kills drop
 * XP into a bar; when the bar fills the player LEVELS UP and the existing 三選一
 * upgrade picker fires. This is the Vampire-Survivors loop replacing the old
 * "clear a wave → pick" trigger.
 *
 * 鐵則(學習與遊戲分離):this is PURE in-run state. It is created fresh each
 * battle and thrown away on exit. It NEVER touches loadProgress/saveProgress or
 * 修煉值/progress — XP here is a game-only currency that decides WHEN the picker
 * shows, not a learning stat. The class is intentionally Phaser-free so it can
 * be unit-tested without a scene and so there is zero temptation to wire it to
 * persistent storage.
 */

/** XP granted per regular enemy kill. */
export const XP_PER_KILL = 3;

/**
 * Level-up XP threshold curve. Level 1→2 needs BASE xp; each subsequent level
 * costs more (a gentle ramp so a K-3 kid still levels several times per run but
 * the cadence slows down). threshold(n) = round(BASE * GROWTH^(n-1)).
 *
 * 2026-06-06 picker-fatigue fix: BASE 12→24(L1→2 ~8 kills)。
 * 2026-06-13 boss「升級太簡單」: 拉高門檻 — BASE 24→44、GROWTH 1.35→1.45。
 * 2026-06-14 boss「打怪變成太難升級」: 過頭了,回調到中間值 — BASE 44→28、
 * GROWTH 1.45→1.40。L1→2 約 9 殺(搭配敵種倍率 ~6-7 殺)→ 前幾級升得快、有
 * 即時回饋;一場約 5 抽(比 44/1.45 的 ~4 抽多、比原 24/1.35 的 ~6-7 抽少)。
 * 純調 XP 門檻,不碰傷害/敵人數值。
 */
export const LEVEL_BASE_XP = 28;
export const LEVEL_GROWTH = 1.40;

export function xpThresholdForLevel(level: number): number {
  // level is the CURRENT level (1-based). Returns XP needed to reach level+1.
  const n = Math.max(1, Math.floor(level));
  return Math.round(LEVEL_BASE_XP * Math.pow(LEVEL_GROWTH, n - 1));
}

/**
 * Result of feeding XP in: how many level-ups occurred this grant (usually 0 or
 * 1, but a big batch grant could cross two thresholds — the scene fires the
 * picker once per level-up so all are honoured).
 */
export interface AddXpResult {
  leveledUp: boolean;
  levelsGained: number;
}

export class RunXp {
  /** Current level (1-based). Starts at 1. */
  level = 1;
  /** XP accumulated toward the NEXT level (resets to remainder on level-up). */
  xpIntoLevel = 0;
  /** Total XP earned this run (cosmetic / debug only — not persisted). */
  totalXp = 0;

  /** XP needed to go from the current level to the next. */
  get xpForNextLevel(): number {
    return xpThresholdForLevel(this.level);
  }

  /** Progress 0..1 of the current level's bar (for the HUD fill width). */
  get progress01(): number {
    const need = this.xpForNextLevel;
    if (need <= 0) return 0;
    return Math.max(0, Math.min(1, this.xpIntoLevel / need));
  }

  /**
   * Add XP (e.g. on an enemy kill). Returns how many level-ups it triggered.
   * Carries the remainder forward so no XP is wasted across a threshold.
   * Guards against non-positive / NaN input (returns no-op).
   */
  add(amount: number): AddXpResult {
    if (!Number.isFinite(amount) || amount <= 0) {
      return { leveledUp: false, levelsGained: 0 };
    }
    this.xpIntoLevel += amount;
    this.totalXp += amount;
    let levelsGained = 0;
    // Loop in case a single grant crosses multiple thresholds.
    while (this.xpIntoLevel >= this.xpForNextLevel) {
      this.xpIntoLevel -= this.xpForNextLevel;
      this.level += 1;
      levelsGained += 1;
    }
    return { leveledUp: levelsGained > 0, levelsGained };
  }

  /** Reset to a fresh run (level 1, empty bar). Called on battle (re)start. */
  reset(): void {
    this.level = 1;
    this.xpIntoLevel = 0;
    this.totalXp = 0;
  }
}
